import * as _ from 'lodash';

import { PolicyType } from '../../typings/enums';
import { Action, ResourceType } from '../../typings/permissions';
import type { Permission } from '../../typings/permissions';

/**
 * Returns a boolean indicating if the supplied permissions include a valid permission for a given {@link ResourceType} and {@link Action}.
 * @deprecated - useHasPermission instead.
 */
export const userHasPermission = (
  permissions: Permission[],
  resourceType: ResourceType,
  action: Action,
): boolean => permissions.some(
  (permission) => permission.resourceType === resourceType && permission.action === action,
);

/**
 * Whether the user may read issue data filtered to the given teams. True if they can view all projects
 * (`project_any`) — such a user already sees every project's issues, so a team filter is only a narrowing of
 * data they can already access — or if they can view every requested team (`team_any`, or a per-team grant).
 *
 * This is the authorization used by the global issue-read endpoints. It intentionally accepts `project_any`,
 * matching how {@link getViewableTeamIds} treats `project_any` as "all teams" for the issue-counts path, so the
 * two surfaces agree. `team_any` is checked explicitly (unlike `getViewableTeamIds`).
 *
 * `teamIds` follows the query filter contract: an array narrows to those teams (`[]` is an empty no-op filter),
 * and `null` means the unassigned-projects filter — which only `project_any`/`team_any` may view, so a purely
 * team-scoped user is denied.
 */
export const userCanViewTeamsForIssues = (permissions: Permission[], teamIds: number[] | null): boolean => {
  if (
    userHasPermission(permissions, ResourceType.ProjectAny, Action.View) ||
    userHasPermission(permissions, ResourceType.TeamAny, Action.View)
  ) {
    return true;
  }

  // `null` is the unassigned-projects filter; without project_any/team_any a team-scoped user cannot view it.
  if (teamIds === null) {
    return false;
  }

  return teamIds.every((teamId) => permissions.some(
    (permission) => _.isMatch(permission, { resourceType: ResourceType.Team, action: Action.View, teamId }),
  ));
};

/**
 * Whether the user may view a single team: project_any/team_any (can view every team) or a per-team view grant.
 */
export const userCanViewTeam = (permissions: Permission[], teamId: number): boolean =>
  userHasPermission(permissions, ResourceType.ProjectAny, Action.View) ||
  userHasPermission(permissions, ResourceType.TeamAny, Action.View) ||
  permissions.some((permission) => _.isMatch(permission, {
    resourceType: ResourceType.Team,
    action: Action.View,
    teamId,
  }));

/**
 * Return a list of project ids for a given list of permissions.
 * @param permissions list of permissions
 */
export const findViewableProjects = (permissions: Permission[]): string[] => _.compact(
  permissions
    .filter((permission) => permission.resourceType === ResourceType.Project && permission.action === Action.View)
    .map((permission) => permission.projectId),
);

/**
 * Check which projects the user is allowed to view.
 *
 * @param permissions The array of permissions the user has.
 * @returns 'all' if the user can view all projects, otherwise an array with the locators the user can view (empty if none).
 */
export function getViewableProjects(permissions: Permission[]): string[] | 'all' {
  if (userHasPermission(permissions, ResourceType.ProjectAny, Action.View)) {
    return 'all';
  }

  return findViewableProjects(permissions);
}

/**
 * Validates that the user has a certain permission + action combo for a specific project.
 * @param permissions - User permissions array.
 * @param resourceType - The resource type they want to act on.
 * @param action - The action they want to perform
 * @param projectId - The project to check for.
 * @returns A boolean. True if they have permission. False if they do not.
 */
export const userHasPermissionForProject = (
  permissions: Permission[],
  resourceType: ResourceType,
  action: Action,
  projectId: string,
): boolean =>
  permissions.some(
    (permission) => _.isMatch(
      permission,
      {
        resourceType,
        action,
        projectId,
      },
    ),
  );

/**
 * Validates that the user has a certain permission + action combo for a specific release group.
 * @param permissions - User permissions array.
 * @param resourceType - The resource type they want to act on.
 * @param action - The action they want to perform
 * @param releaseGroupId - The release group to check for.
 * @returns A boolean. True if they have permission. False if they do not.
 */
export const userHasPermissionForReleaseGroup = (
  permissions: Permission[],
  resourceType: ResourceType,
  action: Action,
  releaseGroupId: number,
): boolean =>
  permissions.some(
    (permission) => _.isMatch(
      permission,
      {
        resourceType,
        action,
        releaseGroupId,
      },
    ),
  );

/**
 * Helper to determine if the provided permission array allows a user to view a specific Release Group.
 *
 * WARNING: This function does not validate that the release group is in the user's organization.
 * If a user has the permission `(ResourceType.ReleaseGroupAny, Action.View)` then this will allow them to view release
 * groups belonging to other organizations.
 *
 * @param permissions Array of Perimissions.
 * @param releaseGroupId The ID of the release group attempting to be viewed.
 * @returns True if the user can see the RG. False if not.
 */
export const userCanViewReleaseGroup = (permissions: Permission[], releaseGroupId: number) =>
  userHasPermission(permissions, ResourceType.ReleaseGroupAny, Action.View) ||
  userHasPermissionForReleaseGroup(permissions, ResourceType.ReleaseGroup, Action.View, releaseGroupId);

/**
 * Helper to determine if the provided permission array allows a user to view a specific Project.
 *
 * NOTE: This only works for project's in the user's own organization, for which they can have RBAC permissions.
 * To validate whether a user should have access to view a public project, consider using
 * `ProjectManagerV2.userCanViewProjectOrIsPublic`.
 *
 * @param permissions Array of Perimissions.
 * @param projectId The ID of the project attempting to be viewed.
 * @returns True if the user can see the Project. False if not.
 */
export const userHasViewPermissionInProject = (permissions: Permission[], projectId: string) =>
  userHasPermission(permissions, ResourceType.ProjectAny, Action.View) ||
  userHasPermissionForProject(permissions, ResourceType.Project, Action.View, projectId);

/**
 * userPermittedPolicyTypes returns the policy types on which the user is permitted to perform the specified action.
 */
export const userPermittedPolicyTypes = (permissions: Permission[], action: Action): PolicyType[] => {
  const policyTypes: PolicyType[] = [];
  if (userHasPermission(permissions, ResourceType.LicensingPolicyAny, action)) {
    policyTypes.push(PolicyType.LICENSING);
  }
  if (userHasPermission(permissions, ResourceType.SBOMPolicyAny, action)) {
    policyTypes.push(PolicyType.SBOM);
  }
  if (userHasPermission(permissions, ResourceType.SecurityPolicyAny, action)) {
    policyTypes.push(PolicyType.SECURITY);
  }
  if (userHasPermission(permissions, ResourceType.QualityPolicyAny, action)) {
    policyTypes.push(PolicyType.QUALITY);
  }
  return policyTypes;
};

/**
 * userPermittedPolicyTypesFromProjects returns the policy types on which the user is permitted to perform the specified action
 * for Project level policies.
 */
export const userPermittedPolicyTypesFromTeam = (permissions: Permission[], action: Action): PolicyType[] => {
  const policyTypes = new Set<PolicyType>();
  if (userHasPermission(permissions, ResourceType.ProjectLicensingPolicy, action)) {
    policyTypes.add(PolicyType.LICENSING);
  }
  if (userHasPermission(permissions, ResourceType.ProjectSBOMPolicy, action)) {
    policyTypes.add(PolicyType.SBOM);
  }
  if (userHasPermission(permissions, ResourceType.ProjectSecurityPolicy, action)) {
    policyTypes.add(PolicyType.SECURITY);
  }
  if (userHasPermission(permissions, ResourceType.ProjectQualityPolicy, action)) {
    policyTypes.add(PolicyType.QUALITY);
  }
  if (userHasPermission(permissions, ResourceType.ReleaseGroupLicensingPolicy, action)) {
    policyTypes.add(PolicyType.LICENSING);
  }
  if (userHasPermission(permissions, ResourceType.ReleaseGroupSecurityPolicy, action)) {
    policyTypes.add(PolicyType.SECURITY);
  }
  if (userHasPermission(permissions, ResourceType.ReleaseGroupQualityPolicy, action)) {
    policyTypes.add(PolicyType.QUALITY);
  }
  return Array.from(policyTypes);
};
